<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <Toast />

        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Счета</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Текущие счета, история операций и аналитика по транзакциям</p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <Button
              @click="toggleTransactionHistory()"
              :icon="showTransactionHistory ? 'pi pi-eye-slash' : 'pi pi-table'"
              :label="showTransactionHistory ? 'Скрыть историю' : 'История транзакций'"
              severity="secondary"
              outlined
              class="!px-3 !py-2 !text-sm !shadow-sm"
            />
            <Button @click="newAccountDialog = true" icon="pi pi-plus" label="Добавить счет" severity="success" class="!px-3 !py-2 !text-sm !shadow-sm" />
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto pr-1">
          <section class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <article
              v-for="card in overviewCards"
              :key="card.key"
              class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div class="mb-2 flex items-center justify-between gap-3">
                <i :class="[card.icon, 'text-lg', card.iconColor]"></i>
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ card.meta }}</span>
              </div>
              <p class="text-[28px] font-semibold leading-none text-slate-900">{{ card.value }}</p>
              <p class="mt-2 text-sm text-slate-500">{{ card.label }}</p>
            </article>
          </section>

          <section class="mb-4 grid gap-4 2xl:grid-cols-[minmax(0,1.3fr)_minmax(360px,0.9fr)]">
            <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div class="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Счета</h3>
                  <p class="mt-1 text-sm text-slate-500">Текущие остатки по счетам и ручные операции</p>
                </div>
              </div>

              <DataTable
                ref="accountTable"
                :filters="filters"
                filterDisplay="menu"
                :globalFilterFields="['bank', 'account', 'currency', 'amount']"
                :value="accounts"
                resizableColumns
                columnResizeMode="fit"
                removableSort
                showGridlines
                stripedRows
                size="small"
                scrollable
                scrollHeight="420px"
                class="surface-table h-full"
              >
                <template #header>
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div class="max-w-md flex-1">
                      <IconField class="w-full">
                        <InputIcon>
                          <i class="pi pi-search" />
                        </InputIcon>
                        <InputText v-model="filters['global'].value" class="w-full" placeholder="Поиск по банку, счету или валюте..." />
                      </IconField>
                    </div>
                    <Button type="button" icon="pi pi-filter-slash" label="Сбросить" variant="outlined" severity="secondary" @click="clearFilter()" />
                  </div>
                </template>
                <template #empty>Не найдено.</template>
                <Column field="bank" header="Банк" style="min-width: 14rem" />
                <Column field="account" header="Номер счета" style="min-width: 14rem" />
                <Column field="currency" header="Валюта" style="min-width: 10rem" />
                <Column field="amount" header="Сумма" style="min-width: 12rem" class="!text-end">
                  <template #body="{ data }">
                    {{ moneyFormatted(data.amount) + ' ₸' }}
                  </template>
                </Column>
                <Column header="Действия" style="width: 8rem" class="!text-center">
                  <template #body="{ data }">
                    <div class="flex items-center justify-center gap-2">
                      <Button icon="pi pi-angle-double-up" variant="outlined" @click="replenishment(data.id)" severity="success" v-tooltip.top="{ value: 'Пополнение' }"></Button>
                      <Button icon="pi pi-arrow-right-arrow-left" variant="outlined" @click="openTransferDialog(data.id)" severity="info" v-tooltip.top="{ value: 'Перевод' }"></Button>
                      <Button icon="pi pi-angle-double-down" variant="outlined" @click="withdrawal(data.id)" severity="danger" v-tooltip.top="{ value: 'Снятие' }"></Button>
                    </div>
                  </template>
                </Column>
                <ColumnGroup type="footer">
                  <Row>
                    <Column footer="Итого:" :colspan="3" footerStyle="text-align:right" />
                    <Column :footer="formattedTotal" footerStyle="text-align:right" />
                    <Column />
                  </Row>
                </ColumnGroup>
              </DataTable>
            </article>

            <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900">Аналитика транзакций</h3>
                <p class="mt-1 text-sm text-slate-500">Суммы и количество операций по каждому типу</p>
              </div>

              <div v-if="!transactionTypeGroups.length" class="flex h-[340px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                Транзакции пока отсутствуют
              </div>

              <template v-else>
                <VueApexCharts
                  class="transaction-chart"
                  type="bar"
                  height="320"
                  :options="transactionChartOptions"
                  :series="transactionChartSeries"
                />

                <div class="mt-4 grid gap-3 sm:grid-cols-2">
                  <div
                    v-for="card in transactionTypeCards"
                    :key="card.key"
                    class="rounded-2xl border px-3 py-3"
                    :class="[card.borderClass, card.bgClass]"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <p class="text-sm font-semibold text-slate-900">{{ card.label }}</p>
                      <span class="text-xs font-medium" :class="card.textClass">{{ card.meta }}</span>
                    </div>
                    <p class="mt-2 text-lg font-semibold text-slate-900">{{ card.value }}</p>
                  </div>
                </div>
              </template>
            </article>
          </section>

          <section
            v-if="showTransactionHistory"
            ref="transactionsSection"
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5"
          >
            <div class="mb-4 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">История транзакций</h3>
                <p class="mt-1 text-sm text-slate-500">Доходы, расходы, пополнения, снятия и оплата труда по счетам</p>
              </div>

              <div class="flex w-full flex-col gap-2 sm:flex-row xl:max-w-3xl xl:justify-end">
                <IconField class="w-full sm:flex-1">
                  <InputIcon>
                    <i class="pi pi-search" />
                  </InputIcon>
                  <InputText v-model.trim="transactionSearch" class="w-full" placeholder="Поиск по дате, счету, банку или типу..." />
                </IconField>
                <Select
                  v-model="selectedTransactionType"
                  :options="transactionTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Все типы"
                  showClear
                  class="w-full sm:w-56"
                />
                <Button
                  type="button"
                  icon="pi pi-filter-slash"
                  label="Очистить"
                  variant="outlined"
                  severity="secondary"
                  class="w-full sm:w-auto"
                  @click="clearTransactionFilters()"
                />
              </div>
            </div>

            <DataTable
              :value="filteredTransactions"
              resizableColumns
              columnResizeMode="fit"
              removableSort
              showGridlines
              stripedRows
              size="small"
              scrollable
              scrollHeight="460px"
              class="surface-table transaction-table"
            >
              <template #empty>Транзакции не найдены.</template>
              <Column field="transaction_date" sortable header="Дата" style="min-width: 11rem">
                <template #body="{ data }">
                  {{ formatDateTime(data.transaction_date) }}
                </template>
              </Column>
              <Column field="account" sortable header="Счет" style="min-width: 12rem"></Column>
              <Column field="bank" sortable header="Банк" style="min-width: 12rem"></Column>
              <Column field="currency" sortable header="Валюта" style="min-width: 8rem"></Column>
              <Column field="transaction_type" sortable header="Тип операции" style="min-width: 12rem">
                <template #body="{ data }">
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                    :class="[getTransactionTypeMeta(data.transaction_type).bgClass, getTransactionTypeMeta(data.transaction_type).textClass]"
                  >
                    {{ data.transaction_type || 'Не указано' }}
                  </span>
                </template>
              </Column>
              <Column field="amount" sortable header="Сумма" style="min-width: 10rem" class="!text-end">
                <template #body="{ data }">
                  <span :class="getTransactionTypeMeta(data.transaction_type).amountClass">
                    {{ moneyFormatted(data.amount) }} ₸
                  </span>
                </template>
              </Column>
            </DataTable>
          </section>
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="replenishmentDialog" :style="{ width: '500px' }" header="Пополнение на счет" :modal="true">
    <div class="flex justify-center items-center gap-4">
      <div class="w-full">
        <div class="grid grid-cols-12 justify-between gap-4 mb-6">
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="bank" class="block"> Банк </label>
              <InputText id="bank" v-model.trim="replenishmentAccount.bank" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="account" class="block"> Номер счета </label>
              <InputText id="account" v-model.trim="replenishmentAccount.account" disabled fluid />
            </FloatLabel>
          </div>
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="currency" class="block"> Валюта </label>
              <InputText id="currency" v-model.trim="replenishmentAccount.currency" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="amount" class="block z-10"> Тек. сумма </label>
              <InputNumber id="amount" v-model.trim="replenishmentAccount.amount" disabled fluid mode="decimal" />
            </FloatLabel>
          </div>
        </div>
        <div class="py-1">
          <FloatLabel variant="over">
            <label for="replenishment" class="block z-10"> Пополнить </label>
            <InputNumber
              v-model.trim="replenishmentAccount.replenishment"
              inputId="replenishment"
              :invalid="replenishmentSubmitted && !replenishmentAccount.replenishment"
              fluid
              required
              mode="decimal"
              :min="100"
            />
          </FloatLabel>
        </div>
        <small v-if="replenishmentSubmitted && !replenishmentAccount.replenishment" class="text-red-500">Заполните!</small>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="replenishmentDialog = false, replenishmentSubmitted = false" severity="danger" fluid />
        <Button label="Пополнить" icon="pi pi-check" @click="replenishmentToAccount()" severity="success" fluid />
      </div>
    </template>
  </Dialog>

  <Dialog v-model:visible="withdrawalDialog" :style="{ width: '500px' }" header="Снятие со счета" :modal="true">
    <div class="flex justify-center items-center gap-4">
      <div class="w-full">
        <div class="grid grid-cols-12 justify-between gap-4 mb-6">
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="bank" class="block"> Банк </label>
              <InputText id="bank" v-model.trim="withdrawalAccount.bank" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="account" class="block"> Номер счета </label>
              <InputText id="account" v-model.trim="withdrawalAccount.account" disabled fluid />
            </FloatLabel>
          </div>
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="currency" class="block"> Валюта </label>
              <InputText id="currency" v-model.trim="withdrawalAccount.currency" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="amount" class="block z-10"> Тек. сумма </label>
              <InputNumber id="amount" v-model.trim="withdrawalAccount.amount" disabled fluid mode="decimal" />
            </FloatLabel>
          </div>
        </div>
        <div class="py-1">
          <FloatLabel variant="over">
            <label for="replenishment" class="block z-10"> Снять </label>
            <InputNumber
              v-model.trim="withdrawalAccount.withdrawal"
              inputId="replenishment"
              :invalid="withdrawalSubmitted && !withdrawalAccount.withdrawal"
              fluid
              required
              mode="decimal"
              :min="100"
            />
          </FloatLabel>
        </div>
        <small v-if="withdrawalSubmitted && !withdrawalAccount.withdrawal" class="text-red-500">Заполните!</small>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="withdrawalDialog = false, withdrawalSubmitted = false" severity="danger" fluid />
        <Button label="Снять" icon="pi pi-check" @click="withdrawalToAccount()" severity="success" fluid />
      </div>
    </template>
  </Dialog>

  <Dialog v-model:visible="transferDialog" :style="{ width: '560px' }" header="Перевод между счетами" :modal="true">
    <div class="flex justify-center items-center gap-4">
      <div class="w-full">
        <div class="grid grid-cols-12 justify-between gap-4 mb-6">
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="transfer-bank" class="block"> Банк списания </label>
              <InputText id="transfer-bank" v-model.trim="transferAccount.bank" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="transfer-account" class="block"> Счет списания </label>
              <InputText id="transfer-account" v-model.trim="transferAccount.account" disabled fluid />
            </FloatLabel>
          </div>
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="in" class="col-span-6">
              <label for="transfer-currency" class="block"> Валюта </label>
              <InputText id="transfer-currency" v-model.trim="transferAccount.currency" disabled fluid />
            </FloatLabel>
            <FloatLabel variant="in" class="col-span-6">
              <label for="transfer-amount" class="block z-10"> Тек. сумма </label>
              <InputNumber id="transfer-amount" v-model.trim="transferAccount.amount" disabled fluid mode="decimal" />
            </FloatLabel>
          </div>
        </div>

        <div class="grid gap-4">
          <FloatLabel variant="over" class="my-3">
            <label for="destination-account" class="block z-10"> Счет зачисления </label>
            <Select
              id="destination-account"
              v-model="transferAccount.destination_account_id"
              :options="transferTargetAccounts"
              optionLabel="label"
              optionValue="id"
              :invalid="transferSubmitted && !transferAccount.destination_account_id"

              fluid
              filter
            />
          </FloatLabel>

          <FloatLabel variant="over">
            <label for="transfer-value" class="block z-10"> Сумма перевода </label>
            <InputNumber
              v-model.trim="transferAccount.amount_to_transfer"
              inputId="transfer-value"
              :invalid="transferSubmitted && !transferAccount.amount_to_transfer"
              fluid
              required
              mode="decimal"
              :min="1"
            />
          </FloatLabel>
        </div>

        <small v-if="transferSubmitted && !transferAccount.destination_account_id" class="mt-2 block text-red-500">Выберите счет зачисления</small>
        <small v-if="transferSubmitted && !transferAccount.amount_to_transfer" class="mt-1 block text-red-500">Заполните сумму перевода</small>
        <small v-if="transferTargetAccounts.length === 0" class="mt-2 block text-amber-600">Нет доступных счетов для перевода в той же валюте.</small>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="closeTransferDialog" severity="danger" fluid />
        <Button label="Перевести" icon="pi pi-check" @click="transferBetweenAccounts()" severity="success" fluid />
      </div>
    </template>
  </Dialog>

  <Dialog v-model:visible="newAccountDialog" :style="{ width: '500px' }" header="Добавить новый счет" :modal="true">
    <div class="flex justify-center items-center gap-4">
      <div class="w-full">
        <div class="grid grid-cols-12 justify-between gap-4 mb-6">
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="on" class="col-span-6 mt-2">
              <label for="bank" class="block z-10"> Банк </label>
              <Select id="bank" v-model="newAccount.bank_id" :options="banks" :invalid="newAccountSubmitted && !newAccount.bank" optionLabel="name" optionValue="id" fluid required></Select>
            </FloatLabel>
            <FloatLabel variant="on" class="col-span-6 mt-2">
              <label for="account" class="block z-10"> Номер счета </label>
              <InputNumber id="account" :min="1000" v-model.trim="newAccount.account" :invalid="newAccountSubmitted && !newAccount.account" fluid required />
            </FloatLabel>
          </div>
          <div class="flex items-center gap-2 col-span-12">
            <FloatLabel variant="on" class="col-span-6 mt-2">
              <label for="account" class="block z-10"> Валюта </label>
              <Select id="currency" v-model="newAccount.currency_id" :options="currencies" :invalid="newAccountSubmitted && !newAccount.currency_id" optionLabel="name" optionValue="id" required fluid></Select>
            </FloatLabel>
            <FloatLabel variant="on" class="mt-2">
              <label for="replenishment" class="block z-10"> Cумма </label>
              <InputNumber v-model.trim="newAccount.amount" inputId="replenishment" :disabled="!newAccount.currency_id" :invalid="newAccountSubmitted && !newAccount.amount" fluid required :min="100" />
            </FloatLabel>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="newAccountDialog = false, newAccountSubmitted = false" severity="danger" fluid />
        <Button label="Добавить" icon="pi pi-check" @click="createAccount()" severity="success" fluid />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue'
import axios from '../lib/axios'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useToast } from 'primevue/usetoast';
import Toast from 'primevue/toast';
import VueApexCharts from 'vue3-apexcharts'

const toast = useToast()
const accountTable = ref()
const transactionsSection = ref(null)

const filters = ref([])
const accounts = ref([])
const banks = ref([])
const currencies = ref([])
const transactions = ref([])
const transactionSearch = ref('')
const selectedTransactionType = ref(null)
const showTransactionHistory = ref(false)

const newAccountDialog = ref(false)
const newAccount = ref({})
const newAccountSubmitted = ref(false)
const withdrawalDialog = ref(false)
const withdrawalAccount = ref({})
const withdrawalSubmitted = ref(false)
const replenishmentDialog = ref(false)
const replenishmentAccount = ref({})
const replenishmentSubmitted = ref(false)
const transferDialog = ref(false)
const transferAccount = ref({})
const transferSubmitted = ref(false)

const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    };
};
initFilters();

const clearFilter = () => {
    initFilters();
};

const clearTransactionFilters = () => {
  transactionSearch.value = ''
  selectedTransactionType.value = null
}

const replenishment = (id) => {
  replenishmentDialog.value = true
  replenishmentAccount.value = accounts.value.find(acc => acc.id === id)
  replenishmentAccount.value.transaction_type_id = 3
}

const replenishmentToAccount = async () => {
    replenishmentSubmitted.value = true;

  if (!replenishmentAccount.value.replenishment) {
    toast.add({severity:'error', summary: 'Заполните!', detail: 'Сумма пополнения не заполнено', life: 3000})
  } else {
    await axios.post('/api/account-replenishment', replenishmentAccount.value)
    await refreshPageData()
    replenishmentAccount.value = {}
    replenishmentDialog.value = false
    replenishmentSubmitted.value = false;
    toast.add({severity:'success', summary: 'Успешно!', detail: 'Пополнение на счет прошло успешно', life: 3000})
  }
}

const withdrawal = async (id) => {
  withdrawalDialog.value = true
  withdrawalAccount.value = accounts.value.find(acc => acc.id === id)
  withdrawalAccount.value.transaction_type_id = 4
}

const withdrawalToAccount = async () => {
  withdrawalSubmitted.value = true;

  if (!withdrawalAccount.value.withdrawal) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Сумма вывода не заполнено', life: 5000})
  } else if (withdrawalAccount.value.withdrawal > withdrawalAccount.value.amount) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Недостаточно средств', life: 5000})
  } else {
    if (withdrawalAccount.value.withdrawal == withdrawalAccount.value.amount) {
    }
    await axios.post('/api/account-withdrawal', withdrawalAccount.value)
    await refreshPageData()
    withdrawalAccount.value = {}
    withdrawalDialog.value = false
    withdrawalSubmitted.value = false
    toast.add({severity:'success', summary: 'Успешно!', detail: 'Снятие со счета прошло успешно', life: 3000})
  }
}

const closeTransferDialog = () => {
  transferDialog.value = false
  transferSubmitted.value = false
  transferAccount.value = {}
}

const openTransferDialog = (id) => {
  const source = accounts.value.find((acc) => acc.id === id)
  if (!source) return

  transferAccount.value = {
    ...source,
    source_account_id: source.id,
    destination_account_id: null,
    amount_to_transfer: null,
  }
  transferDialog.value = true
}

const transferTargetAccounts = computed(() => {
  if (!transferAccount.value?.source_account_id) return []

  return accounts.value
    .filter((account) => account.id !== transferAccount.value.source_account_id)
    .filter((account) => Number(account.currency_id) === Number(transferAccount.value.currency_id))
    .map((account) => ({
      ...account,
      label: `${account.bank} · ${account.account} · ${account.currency}`,
    }))
})

const transferBetweenAccounts = async () => {
  transferSubmitted.value = true

  if (!transferAccount.value.destination_account_id || !transferAccount.value.amount_to_transfer) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Заполните счет назначения и сумму перевода', life: 4000})
    return
  }

  if (Number(transferAccount.value.amount_to_transfer) > Number(transferAccount.value.amount || 0)) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Недостаточно средств для перевода', life: 4000})
    return
  }

  try {
    await axios.post('/api/account-transfer', {
      source_account_id: transferAccount.value.source_account_id,
      destination_account_id: transferAccount.value.destination_account_id,
      amount: transferAccount.value.amount_to_transfer,
    })

    await refreshPageData()
    closeTransferDialog()
    toast.add({severity:'success', summary: 'Успешно!', detail: 'Перевод между счетами выполнен', life: 3000})
  } catch (error) {
    toast.add({
      severity:'error',
      summary: 'Ошибка!',
      detail: error?.response?.data?.message || 'Не удалось выполнить перевод',
      life: 5000
    })
  }
}

const createAccount = async () => {
  newAccountSubmitted.value = true
  let account = accounts.value.find(acc => acc.account == newAccount.value.account);

  if (!newAccount.value.account || !newAccount.value.amount || !newAccount.value.bank_id || !newAccount.value.currency_id) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Поля не заполнены', life: 5000})
  } else if (account) {
    toast.add({severity:'error', summary: 'Ошибка!', detail: 'Счет уже существует', life: 5000})
  } else {
    await axios.post('/api/account', newAccount.value)

    toast.add({severity:'success', summary: 'Успешно!', detail: 'Счет создан', life: 3000})
    await refreshPageData()
    newAccount.value = {}
    newAccountSubmitted.value = false
    newAccountDialog.value = false
  }
}

const moneyFormatted = (amount) => {
  return new Intl.NumberFormat('ru-RU', { style: 'decimal', minimumFractionDigits: 0 }).format(Number(amount || 0))
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}.${month}.${year} ${hours}:${minutes}`
}

const transactionTypeMeta = {
  'Доход': {
    icon: 'pi pi-arrow-down-left',
    iconColor: 'text-emerald-700',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-200',
    textClass: 'text-emerald-700',
    amountClass: 'font-semibold text-emerald-700',
    flow: 'in',
  },
  'Пополнение': {
    icon: 'pi pi-plus-circle',
    iconColor: 'text-sky-700',
    bgClass: 'bg-sky-50',
    borderClass: 'border-sky-200',
    textClass: 'text-sky-700',
    amountClass: 'font-semibold text-sky-700',
    flow: 'in',
  },
  'Расход': {
    icon: 'pi pi-arrow-up-right',
    iconColor: 'text-rose-700',
    bgClass: 'bg-rose-50',
    borderClass: 'border-rose-200',
    textClass: 'text-rose-700',
    amountClass: 'font-semibold text-rose-700',
    flow: 'out',
  },
  'Снятие': {
    icon: 'pi pi-minus-circle',
    iconColor: 'text-orange-700',
    bgClass: 'bg-orange-50',
    borderClass: 'border-orange-200',
    textClass: 'text-orange-700',
    amountClass: 'font-semibold text-orange-700',
    flow: 'out',
  },
  'Оплата труда': {
    icon: 'pi pi-briefcase',
    iconColor: 'text-violet-700',
    bgClass: 'bg-violet-50',
    borderClass: 'border-violet-200',
    textClass: 'text-violet-700',
    amountClass: 'font-semibold text-violet-700',
    flow: 'out',
  },
  'Перевод': {
    icon: 'pi pi-arrow-right-arrow-left',
    iconColor: 'text-cyan-700',
    bgClass: 'bg-cyan-50',
    borderClass: 'border-cyan-200',
    textClass: 'text-cyan-700',
    amountClass: 'font-semibold text-cyan-700',
    flow: 'neutral',
  },
}

const getTransactionTypeMeta = (type) => {
  return transactionTypeMeta[type] || {
    icon: 'pi pi-chart-bar',
    iconColor: 'text-slate-700',
    bgClass: 'bg-slate-50',
    borderClass: 'border-slate-200',
    textClass: 'text-slate-700',
    amountClass: 'font-semibold text-slate-700',
    flow: 'neutral',
  }
}

const transactionTypeOrder = ['Доход', 'Расход', 'Пополнение', 'Снятие', 'Перевод', 'Оплата труда']

const totalBalance = computed(() => {
  return accounts.value.reduce((acc, item) => acc + (Number(item.amount || 0) * Number(item.rate || 0)), 0)
})

const formattedTotal = computed(() => {
  return `${moneyFormatted(totalBalance.value)} ₸`
})

const normalizedTransactions = computed(() => {
  return (transactions.value || []).map((item) => ({
    ...item,
    amount: Number(item.amount || 0),
    transaction_type: String(item.transaction_type || 'Не указано').trim() || 'Не указано',
  }))
})

const transactionTypeGroups = computed(() => {
  const map = new Map()

  normalizedTransactions.value.forEach((item) => {
    const key = item.transaction_type
    const current = map.get(key) || {
      type: key,
      amount: 0,
      count: 0,
    }

    current.amount += Number(item.amount || 0)
    current.count += 1
    map.set(key, current)
  })

  return [...map.values()].sort((a, b) => {
    const aIndex = transactionTypeOrder.indexOf(a.type)
    const bIndex = transactionTypeOrder.indexOf(b.type)

    if (aIndex === -1 && bIndex === -1) {
      return a.type.localeCompare(b.type, 'ru')
    }
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })
})

const transactionTypeOptions = computed(() => {
  return transactionTypeGroups.value.map((item) => ({
    label: item.type,
    value: item.type,
  }))
})

const filteredTransactions = computed(() => {
  const query = transactionSearch.value.trim().toLowerCase()

  return normalizedTransactions.value.filter((item) => {
    const typeMatch = !selectedTransactionType.value || item.transaction_type === selectedTransactionType.value
    if (!typeMatch) return false

    if (!query) return true

    return [
      item.transaction_date,
      item.account,
      item.bank,
      item.currency,
      item.transaction_type,
      item.amount,
    ].some((value) => String(value || '').toLowerCase().includes(query))
  })
})

const inflowAmount = computed(() => {
  return transactionTypeGroups.value
    .filter((item) => getTransactionTypeMeta(item.type).flow === 'in')
    .reduce((sum, item) => sum + item.amount, 0)
})

const outflowAmount = computed(() => {
  return transactionTypeGroups.value
    .filter((item) => getTransactionTypeMeta(item.type).flow === 'out')
    .reduce((sum, item) => sum + item.amount, 0)
})

const netFlowAmount = computed(() => inflowAmount.value - outflowAmount.value)

const overviewCards = computed(() => [
  {
    key: 'balance',
    label: 'Баланс на счетах',
    value: formattedTotal.value,
    meta: `${accounts.value.length} счетов`,
    icon: 'pi pi-wallet',
    iconColor: 'text-emerald-700',
  },
  {
    key: 'transactions',
    label: 'Транзакций',
    value: moneyFormatted(normalizedTransactions.value.length),
    meta: 'Всего операций',
    icon: 'pi pi-arrow-right-arrow-left',
    iconColor: 'text-sky-700',
  },
  {
    key: 'inflow',
    label: 'Поступления',
    value: `${moneyFormatted(inflowAmount.value)} ₸`,
    meta: 'Доход и пополнение',
    icon: 'pi pi-arrow-down-left',
    iconColor: 'text-emerald-700',
  },
  {
    key: 'outflow',
    label: 'Списания',
    value: `${moneyFormatted(outflowAmount.value)} ₸`,
    meta: 'Расход, снятие, ЗП',
    icon: 'pi pi-arrow-up-right',
    iconColor: 'text-rose-700',
  },
  {
    key: 'net',
    label: 'Чистый поток',
    value: `${netFlowAmount.value < 0 ? '−' : ''}${moneyFormatted(Math.abs(netFlowAmount.value))} ₸`,
    meta: netFlowAmount.value >= 0 ? 'Положительный' : 'Отрицательный',
    icon: 'pi pi-chart-line',
    iconColor: netFlowAmount.value >= 0 ? 'text-emerald-700' : 'text-orange-700',
  },
  {
    key: 'types',
    label: 'Типов операций',
    value: moneyFormatted(transactionTypeGroups.value.length),
    meta: 'Уникальные типы',
    icon: 'pi pi-tags',
    iconColor: 'text-violet-700',
  },
])

const transactionTypeCards = computed(() => {
  return transactionTypeGroups.value.map((item) => {
    const meta = getTransactionTypeMeta(item.type)
    return {
      key: item.type,
      label: item.type,
      value: `${moneyFormatted(item.amount)} ₸`,
      meta: `${moneyFormatted(item.count)} опер.`,
      ...meta,
    }
  })
})

const transactionChartSeries = computed(() => [
  {
    name: 'Сумма',
    data: transactionTypeGroups.value.map((item) => item.amount),
  },
])

const transactionChartOptions = computed(() => ({
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
  },
  colors: transactionTypeGroups.value.map((item) => {
    const type = item.type
    if (type === 'Доход') return '#0f766e'
    if (type === 'Пополнение') return '#0284c7'
    if (type === 'Расход') return '#e11d48'
    if (type === 'Снятие') return '#ea580c'
    if (type === 'Оплата труда') return '#7c3aed'
    return '#475569'
  }),
  plotOptions: {
    bar: {
      horizontal: true,
      borderRadius: 6,
      barHeight: '52%',
      distributed: true,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    borderColor: '#e2e8f0',
    strokeDashArray: 4,
  },
  xaxis: {
    categories: transactionTypeGroups.value.map((item) => item.type),
    labels: {
      formatter: (value) => moneyFormatted(value),
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#475569',
        fontSize: '12px',
      },
    },
  },
  tooltip: {
    y: {
      formatter: (value, { dataPointIndex }) => {
        const item = transactionTypeGroups.value[dataPointIndex]
        return `${moneyFormatted(value)} ₸ · ${moneyFormatted(item?.count || 0)} опер.`
      },
    },
  },
}))

const fetchAccount = async () => {
  const res = await axios.get('/api/account')
  accounts.value = Array.isArray(res.data) ? res.data : []
}

const fetchTransactions = async () => {
  const res = await axios.get('/api/transactions')
  transactions.value = Array.isArray(res.data) ? res.data : []
}

const refreshPageData = async () => {
  try {
    await Promise.all([
      fetchAccount(),
      fetchTransactions(),
    ])
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось загрузить данные по счетам',
      life: 3000,
    })
  }
}

const getBanks = async () => {
  const res = await axios.get('/api/banks')
  banks.value = res.data
}
const getCurrencies = async () => {
  const res = await axios.get('/api/currencies')
  currencies.value = res.data
}

const scrollToTransactions = () => {
  transactionsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const toggleTransactionHistory = async () => {
  showTransactionHistory.value = !showTransactionHistory.value

  if (showTransactionHistory.value) {
    await nextTick()
    scrollToTransactions()
  }
}

onMounted(() => {
  refreshPageData()
  getBanks()
  getCurrencies()
})
</script>

<style scoped>
:deep(.surface-table .p-datatable-table-container) {
  border-radius: 1rem;
  border: 1px solid rgb(226 232 240);
}

:deep(.surface-table .p-datatable-thead > tr > th) {
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.9rem 1rem;
}

:deep(.surface-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(30 41 59);
  padding: 0.9rem 1rem;
}

:deep(.surface-table .p-datatable-tbody > tr:hover) {
  background: rgba(240, 253, 250, 0.7);
}

:deep(.surface-table .p-paginator) {
  border: 0;
  border-top: 1px solid rgb(226 232 240);
  background: white;
  padding-top: 0.75rem;
}

:deep(.surface-table .p-datatable-footer),
:deep(.surface-table .p-datatable-tfoot > tr > td) {
  background: rgb(248 250 252);
  color: rgb(15 23 42);
  font-weight: 600;
  border-color: rgb(226 232 240);
}

:deep(.transaction-chart .apexcharts-tooltip) {
  border-radius: 0.9rem;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}
</style>
